import { ReactNode, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import { logout, me } from "../api/auth";
import { useAuth } from "../stores/auth";

type NavItem = {
  to: string;
  label: string;
  disabled?: boolean;
  hideWhenExempt?: boolean;
  staffOnly?: boolean;
};

const NAV: NavItem[] = [
  { to: "/dashboard", label: "Overview" },
  { to: "/computers", label: "Computers" },
  { to: "/fixed-addresses", label: "Fixed addresses" },
  { to: "/sharing/folders", label: "Sharing" },
  { to: "/backups", label: "Backups" },
  { to: "/update", label: "Update KraetdNet" },
  { to: "/billing", label: "Billing", disabled: true, hideWhenExempt: true },
  { to: "/admin/support", label: "Support sessions", staffOnly: true },
  { to: "/admin/installer", label: "Installer admin", staffOnly: true },
  { to: "/settings", label: "Settings", disabled: true },
];

type Props = {
  children: ReactNode;
};

export default function AppShell({ children }: Props) {
  const navigate = useNavigate();
  const tenant = useAuth((s) => s.tenant);
  const user = useAuth((s) => s.user);
  const refreshToken = useAuth((s) => s.refreshToken);
  const trialExpired = useAuth((s) => s.trialExpired);
  const setProfile = useAuth((s) => s.setProfile);
  const clear = useAuth((s) => s.clear);

  useEffect(() => {
    if (user && tenant) return;
    let cancelled = false;
    me()
      .then((data) => {
        if (!cancelled) setProfile(data);
      })
      .catch(() => {
        // 401 handled by axios interceptor; other errors leave the shell with empty header.
      });
    return () => {
      cancelled = true;
    };
  }, [user, tenant, setProfile]);

  async function onSignOut() {
    if (refreshToken) {
      try {
        await logout(refreshToken);
      } catch {
        // ignore — we clear locally regardless
      }
    }
    clear();
    navigate("/login");
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="hidden w-60 shrink-0 border-r border-slate-200 bg-white p-6 md:block">
        <div className="text-lg font-semibold text-slate-900">KraetdNet</div>
        <nav className="mt-8 space-y-1">
          {NAV
            .filter((item) => !(item.hideWhenExempt && tenant?.billing_exempt))
            .filter((item) => !(item.staffOnly && !tenant?.billing_exempt))
            .map((item) =>
            item.disabled ? (
              <span
                key={item.to}
                className="block cursor-not-allowed rounded px-3 py-2 text-sm text-slate-400"
                title="Coming in a later release"
              >
                {item.label}
              </span>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `block rounded px-3 py-2 text-sm ${
                    isActive
                      ? "bg-slate-900 text-white"
                      : "text-slate-700 hover:bg-slate-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ),
          )}
          <button
            type="button"
            onClick={onSignOut}
            className="mt-8 block w-full rounded px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100"
          >
            Sign out
          </button>
        </nav>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
          <div>
            <div className="text-sm text-slate-500">Office</div>
            <div className="text-base font-semibold text-slate-900">{tenant?.name ?? "—"}</div>
          </div>
          {tenant?.billing_exempt ? null : (
            <TrialBadge trialEndsAt={tenant?.trial_ends_at ?? null} status={tenant?.status} />
          )}
        </header>
        {trialExpired && !tenant?.billing_exempt && <TrialExpiredBanner />}
        <main className="flex-1 p-6">{children}</main>
      </div>
    </div>
  );
}

function TrialBadge({
  trialEndsAt,
  status,
}: {
  trialEndsAt: string | null;
  status: string | undefined;
}) {
  if (!trialEndsAt || status !== "trial") return null;
  const remaining = Math.max(
    0,
    Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
  );
  return (
    <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-900">
      Free trial — {remaining} day{remaining === 1 ? "" : "s"} left
    </span>
  );
}

function TrialExpiredBanner() {
  return (
    <div
      role="status"
      className="border-b border-amber-200 bg-amber-50 px-6 py-3 text-sm text-amber-900"
    >
      <span className="font-semibold">Your free trial has ended.</span>{" "}
      You can still see everything, but new changes are paused. Email{" "}
      <a className="underline" href="mailto:hello@kraetd.com">hello@kraetd.com</a>{" "}
      to subscribe and keep using KraetdNet.
    </div>
  );
}
